import React, { useCallback, useEffect, useRef, useState } from "react";
import trans from "../../../utils";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { throttle } from "lodash";

const EGYPT_CENTER = { lat: 26.8206, lng: 30.8025 };
// Egypt's bounding box — used as a hard geographic filter (via
// `strictBounds`) for Places Autocomplete, since `componentRestrictions`
// alone can still let a globally famous name (e.g. "Madison Square Garden"
// for a "garden" search) slip through for generic queries.
const EGYPT_BOUNDS = { south: 22.0, west: 24.7, north: 31.7, east: 36.9 };
const DEFAULT_CLICK_ZOOM = 15;
const GOOGLE_MAPS_LIBRARIES = ["places"];
const MAP_CONTAINER_STYLE = {
  height: "220px",
  width: "100%",
  borderRadius: "8px",
};
const MAP_OPTIONS = {
  minZoom: 5,
  maxZoom: 18,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
};
const MARKER_ICON_URL = "https://www.svgrepo.com/show/513317/location-pin.svg";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const fetchGeoCodePosition = async (lat, lng) => {
  try {
    const geocoder = new window.google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    return results?.[0]?.formatted_address || "";
  } catch (error) {
    return "";
  }
};

// ** Click/drag-to-place map for capturing a customer's latitude/longitude,
//    with reverse geocoding (Google Geocoding API) to keep the address bound
//    to the pin, plus a Places Autocomplete search box that resolves straight
//    to a marker + address via the same onPick contract a manual pin drop
//    uses. `latitude`/`longitude`/`zoom` are driven by the Governorate →
//    Markaz → Village cascade upstream; a manual click/drag/search always
//    wins as the final override. Always rendered once a phone lookup
//    resolves (new or existing customer) so an existing customer's saved
//    location is visible; `disabled` makes it read-only — it still shows the
//    current pin, but search/click/drag are all no-ops — mirroring the
//    Address Information section's own Edit-gated disabled state.
export default function CallCenterMapPicker({
  latitude,
  longitude,
  zoom = DEFAULT_CLICK_ZOOM,
  address,
  disabled = false,
  onPick,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [position, setPosition] = useState(
    latitude && longitude
      ? { lat: Number(latitude), lng: Number(longitude) }
      : null,
  );
  const [resolvedAddress, setResolvedAddress] = useState(address || "");
  const [mapError, setMapError] = useState(null);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const throttledFetcher = useRef(
    throttle(async (lat, lng, cb) => {
      const resolved = await fetchGeoCodePosition(lat, lng);
      cb(resolved);
    }, 1000),
  ).current;

  useEffect(() => {
    // Also resets to null when lat/lng are cleared externally (e.g. the
    // phone lookup switching to a different customer/new-customer state) —
    // this component now stays mounted across that transition instead of
    // remounting, so a stale marker must be explicitly cleared here.
    setPosition(
      latitude && longitude
        ? { lat: Number(latitude), lng: Number(longitude) }
        : null,
    );
  }, [latitude, longitude]);

  useEffect(() => {
    setResolvedAddress(address || "");
  }, [address]);

  // Surfaces requests Google accepts at script-load time but rejects at
  // request time (bad HTTP-referrer restriction, API not enabled, billing
  // off) — useJsApiLoader's loadError only catches script-load failures.
  useEffect(() => {
    window.gm_authFailure = () => {
      setMapError(
        trans("Map authentication failed. Please enter the address manually."),
      );
    };
    return () => {
      delete window.gm_authFailure;
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && position) {
      mapRef.current.panTo(position);
      mapRef.current.setZoom(zoom);
    }
  }, [position, zoom]);

  const handleResolvedPick = useCallback(
    (lat, lng, resolved) => {
      setResolvedAddress(resolved);
      onPick({ latitude: lat, longitude: lng, address: resolved });
    },
    [onPick],
  );

  const handleMapClick = useCallback(
    (e) => {
      if (disabled) return; // read-only until the address section's Edit toggle is on
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setPosition({ lat, lng });
      throttledFetcher(lat, lng, (resolved) => handleResolvedPick(lat, lng, resolved));
    },
    [disabled, throttledFetcher, handleResolvedPick],
  );

  const handleMarkerDragEnd = useCallback(
    (e) => {
      if (disabled) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setPosition({ lat, lng });
      throttledFetcher(lat, lng, (resolved) => handleResolvedPick(lat, lng, resolved));
    },
    [disabled, throttledFetcher, handleResolvedPick],
  );

  const handlePlaceSelected = useCallback(() => {
    if (disabled) return; // the input is also `disabled`, but guard defensively
    const place = autocompleteRef.current?.getPlace();
    const location = place?.geometry?.location;
    if (!location) return; // agent hit Enter without picking a suggestion

    const lat = location.lat();
    const lng = location.lng();
    const resolved = place.formatted_address || place.name || "";

    setPosition({ lat, lng });
    setResolvedAddress(resolved);
    onPick({ latitude: lat, longitude: lng, address: resolved });

    // This pick's Place Details lookup closes the current Autocomplete
    // billing session — rotate to a fresh token for the next search so it
    // bills at the flat per-session rate instead of per-keystroke.
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      autocompleteRef.current?.setOptions({ sessionToken: sessionTokenRef.current });
    }
  }, [disabled, onPick]);

  const handleAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    autocomplete.setOptions({
      componentRestrictions: { country: "eg" },
      bounds: EGYPT_BOUNDS,
      strictBounds: true,
      fields: ["formatted_address", "geometry", "name"],
      sessionToken: sessionTokenRef.current,
    });
  }, []);

  if (!GOOGLE_MAPS_API_KEY || loadError || mapError) {
    return (
      <div className="my-60">
        <p className="form-label text-capitalize">
          {trans("Select customer location on map")}
        </p>
        <p style={{ color: "#d4380d", marginBottom: 0 }}>
          {mapError ||
            trans(
              "Unable to load map. Please use Governorate/Markaz/Village or type the address manually.",
            )}
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="my-60">
        <p className="form-label text-capitalize">
          {trans("Select customer location on map")}
        </p>
        <div
          style={{ ...MAP_CONTAINER_STYLE, background: "#f0f0f0" }}
          className="d-flex align-items-center justify-content-center"
        >
          {trans("Loading map...")}
        </div>
      </div>
    );
  }

  return (
    <div className="my-60">
      <p className="form-label text-capitalize">
        {trans("Select customer location on map")}
      </p>
      <Autocomplete onLoad={handleAutocompleteLoad} onPlaceChanged={handlePlaceSelected}>
        <input
          type="text"
          className="form-control mb-2"
          placeholder={trans("Search for a location")}
          disabled={disabled}
        />
      </Autocomplete>
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={position || EGYPT_CENTER}
        zoom={position ? zoom : 6}
        options={MAP_OPTIONS}
        onClick={handleMapClick}
      >
        {position && (
          <Marker
            position={position}
            draggable={!disabled}
            onDragEnd={handleMarkerDragEnd}
            icon={{
              url: MARKER_ICON_URL,
              scaledSize: new window.google.maps.Size(32, 32),
              anchor: new window.google.maps.Point(16, 32),
            }}
          />
        )}
      </GoogleMap>
      <p style={{ color: "#2E9245", marginTop: "6px", marginBottom: 0 }}>
        {resolvedAddress || trans("Address of the selected location")}
      </p>
    </div>
  );
}
